import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useComms } from './context';
import { type LiveCalls, LiveCallsContext } from './liveCallsContext';
import {
  applyCallEvent,
  dropRing,
  type LiveCallQuery,
  type LiveCallsState,
  matchLiveCall,
  NO_LIVE_CALLS,
  sweepStale,
} from './liveCallsState';
import { useNotifications } from './notifications';

/**
 * The module's one ear on the `call.*` socket events.
 *
 * It used to be the incoming-call overlay's, and it held only the rings: a
 * card went up on `call.ringing` and came down on `call.answered`. That threw
 * away the one thing the answered call still needed — its name and its
 * thread — the moment it was answered. Now the record outlives the ring (see
 * liveCalls.ts), and both the overlay (the cards) and the call bar (the strip
 * over the thread for a call that is UP) read from here.
 *
 * This is also the embed relay: EVERY call.* event, both directions, is
 * forwarded to the dashboard through `source.notifyCallHost`, so its own
 * incoming-call widget works on any dashboard page whatever tab the module
 * itself is showing.
 */


/** A card that never hears its end comes down on its own. */
const RING_TIMEOUT_MS = 60_000;

/**
 * How long a card waits for the browser phone's own invite before saying it
 * cannot help. The socket event and the SIP/WebRTC invite race by a beat, and
 * the agent is looking at the card either way; twenty seconds is long enough
 * to cover the race and short enough to still be useful advice.
 */
const INVITE_WAIT_MS = 20_000;

/** A record whose end was never heard is forgotten after this — no call is that long. */
const STALE_AFTER_MS = 6 * 60 * 60 * 1000;
const SWEEP_EVERY_MS = 60_000;

export function LiveCallsProvider({ children }: { children: ReactNode }) {
  const { source } = useComms();
  const { preferences } = useNotifications();
  const [state, setState] = useState<LiveCallsState>(NO_LIVE_CALLS);
  // Kept apart from the rings so the list itself stays the socket's word.
  const [waited, setWaited] = useState<string[]>([]);
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>[]>());

  /**
   * The agent's own "stop ringing me". Read through a ref because the socket
   * subscription below is set up once and must see the current answer, not
   * the one in force when it mounted.
   *
   * It suppresses the FORWARD as well as the card: the dashboard hides its
   * own ring widget on the communication routes, so relaying the event while
   * refusing to draw the card here would just move the noise rather than stop
   * it. call.answered / call.ended still forward, so a card raised before the
   * switch was flipped still clears.
   */
  const ringingOnRef = useRef(true);
  useEffect(() => {
    ringingOnRef.current = preferences?.calls_enabled !== false;
  }, [preferences]);

  useEffect(() => {
    const timers = timersRef.current;
    const clearTimers = (key: string) => {
      for (const timer of timers.get(key) ?? []) clearTimeout(timer);
      timers.delete(key);
    };
    const unsubscribe = source.subscribe((event) => {
      if (!event.name.startsWith('call.')) return;
      // Ringing switched off suppresses the CARD and the forward, never the
      // record: call.ringing is the one event that names the call — its
      // thread, its line — and a store that skipped it could not name the
      // conversation behind a call the agent then took on the dashboard.
      const suppressRing = event.name === 'call.ringing' && !ringingOnRef.current;
      if (!suppressRing) source.notifyCallHost?.(event.name, event.payload);

      const now = Date.now();
      setState((current) => {
        let next = applyCallEvent(current, event.name, event.payload, now);
        if (next === current) return current;
        if (suppressRing) {
          for (const ring of next.rings) {
            if (!current.rings.some((held) => held.key === ring.key)) next = dropRing(next, ring.key);
          }
          return next;
        }
        if (event.name === 'call.ringing') {
          for (const ring of next.rings) {
            if (timers.has(ring.key)) continue;
            timers.set(ring.key, [
              setTimeout(() => {
                clearTimers(ring.key);
                setState((later) => dropRing(later, ring.key));
                setWaited((later) => later.filter((entry) => entry !== ring.key));
              }, RING_TIMEOUT_MS),
              setTimeout(() => setWaited((later) => [...later, ring.key]), INVITE_WAIT_MS),
            ]);
          }
        } else {
          for (const ring of current.rings) {
            if (!next.rings.some((still) => still.key === ring.key)) {
              clearTimers(ring.key);
              setWaited((later) => later.filter((entry) => entry !== ring.key));
            }
          }
        }
        return next;
      });
    });
    const sweep = setInterval(() => setState((current) => sweepStale(current, Date.now(), STALE_AFTER_MS)), SWEEP_EVERY_MS);
    return () => {
      unsubscribe();
      clearInterval(sweep);
      for (const entry of timers.values()) for (const timer of entry) clearTimeout(timer);
      timers.clear();
    };
  }, [source]);

  const dismiss = useCallback((key: string) => {
    // The card's timers go with it: the invite wait would otherwise re-add a
    // card that is no longer there to its waited list.
    for (const timer of timersRef.current.get(key) ?? []) clearTimeout(timer);
    timersRef.current.delete(key);
    setState((current) => dropRing(current, key));
    setWaited((current) => current.filter((entry) => entry !== key));
  }, []);
  const known = state.known;
  const find = useCallback((query: LiveCallQuery) => matchLiveCall(known, query), [known]);

  const value = useMemo<LiveCalls>(
    () => ({ rings: state.rings, waited, dismiss, find }),
    [state.rings, waited, dismiss, find],
  );
  return <LiveCallsContext.Provider value={value}>{children}</LiveCallsContext.Provider>;
}
